/**
 * WaterfallChart Component
 *
 * Displays a waterfall/gantt-style timeline showing when each operation
 * started and how long it took, visualizing the sequential flow.
 */

import React, {useMemo} from 'react';
import {Box, Tooltip, Typography} from '@mui/material';
import {ProfileSession} from '../../@types/profiler';

interface WaterfallChartProps {
    session: ProfileSession;
    height?: number;
}

/**
 * Color palette for waterfall bars
 */
const COLORS = [
    '#2196F3', // Blue
    '#4CAF50', // Green
    '#FF9800', // Orange
    '#9C27B0', // Purple
    '#F44336', // Red
    '#00BCD4', // Cyan
    '#FFC107', // Amber
    '#607D8B', // Blue Grey
    '#E91E63', // Pink
    '#8BC34A', // Light Green
];

const WaterfallChart: React.FC<WaterfallChartProps> = ({ session, height = 200 }) => {
    const rows = useMemo(() => {
        return session.entries.map((entry, index) => {
            const leftPercent = (entry.startTime / session.totalTime) * 100;
            const widthPercent = (entry.duration / session.totalTime) * 100;
            const color = COLORS[index % COLORS.length];

            return {
                ...entry,
                leftPercent,
                widthPercent: Math.max(widthPercent, 0.5), // Minimum width for visibility
                color,
                index
            };
        });
    }, [session]);

    if (!session.entries.length) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">No timing data available</Typography>
            </Box>
        );
    }

    const rowHeight = 24;
    const labelWidth = 180;
    const chartHeight = Math.max(rows.length * rowHeight + 40, height);

    return (
        <Box sx={{ height: chartHeight, overflow: 'auto' }}>
            {/* Header */}
            <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, mb: 1, px: 1 }}
            >
                Timeline Waterfall
            </Typography>

            {/* Time axis */}
            <Box sx={{ display: 'flex', mb: 0.5, px: 1 }}>
                <Box sx={{ width: labelWidth, flexShrink: 0 }} />
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'text.secondary' }}>
                    <span>0ms</span>
                    <span>{(session.totalTime / 4).toFixed(0)}ms</span>
                    <span>{(session.totalTime / 2).toFixed(0)}ms</span>
                    <span>{((session.totalTime * 3) / 4).toFixed(0)}ms</span>
                    <span>{session.totalTime.toFixed(0)}ms</span>
                </Box>
            </Box>

            {/* Rows */}
            {rows.map((row) => (
                <Box
                    key={row.index}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: rowHeight,
                        px: 1,
                        '&:hover': {
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    {/* Label */}
                    <Tooltip title={row.label} placement="left">
                        <Typography
                            variant="caption"
                            sx={{
                                width: labelWidth,
                                flexShrink: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                pr: 1,
                                fontSize: '11px'
                            }}
                        >
                            {row.label}
                        </Typography>
                    </Tooltip>

                    {/* Bar container */}
                    <Box
                        sx={{
                            flex: 1,
                            height: 16,
                            bgcolor: 'grey.100',
                            borderRadius: 0.5,
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Bar */}
                        <Tooltip
                            title={`${row.duration.toFixed(1)}ms (${row.percentage.toFixed(1)}%)`}
                            placement="top"
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${row.leftPercent}%`,
                                    width: `${row.widthPercent}%`,
                                    height: '100%',
                                    bgcolor: row.color,
                                    borderRadius: 0.5,
                                    cursor: 'pointer',
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                        opacity: 0.8
                                    }
                                }}
                            />
                        </Tooltip>
                    </Box>

                    {/* Duration label */}
                    <Typography
                        variant="caption"
                        sx={{
                            width: 60,
                            flexShrink: 0,
                            textAlign: 'right',
                            fontSize: '10px',
                            color: 'text.secondary',
                            pl: 1
                        }}
                    >
                        {row.duration.toFixed(1)}ms
                    </Typography>
                </Box>
            ))}

            {/* Total */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: rowHeight,
                    px: 1,
                    mt: 1,
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        width: labelWidth,
                        flexShrink: 0,
                        fontWeight: 600,
                        fontSize: '11px'
                    }}
                >
                    Total
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography
                    variant="caption"
                    sx={{
                        width: 60,
                        flexShrink: 0,
                        textAlign: 'right',
                        fontWeight: 600,
                        fontSize: '11px'
                    }}
                >
                    {session.totalTime.toFixed(1)}ms
                </Typography>
            </Box>
        </Box>
    );
};

export default React.memo(WaterfallChart);
